import Tsunami from './disasters/Tsunami';
import Earthquake from './disasters/Earthquake';
import Flood from './disasters/Flood';
import common from './common';
import Wildfire from './disasters/Wildfire';
import Hurricane from './disasters/Hurricane';
import { clamp } from './utils';

export default class Scenario {
	constructor(world) {
		this.signals = {};

		this._world = world;
		this._disaster = null;

		this._type = Scenario.TYPE_TSUNAMI;
		this._difficulty = '';
		this._time = 300;
		this._elapsedTime = 0;
		this._countdownTime = Date.now();
		this._ended = false;
		this._score = 0;

		this._objectives = 0;
		this._hasCompletedObjectives = false;

		this._budget = {};
		this._population = {};
		this._targets = {};
		this._targetProgress = {};
		this._limits = {};

		this._ccUpgrades = [];

		this._win = false;
		this._finished = false;

		this.signals = {};
		this.signals.objectivescomplete = new P3.utils.MiniSignal();
		this.signals.timerend = new P3.utils.MiniSignal();

		this._sfx = [];
		this._lastSfxPlay = Date.now();
		this._approachSFX = null;
		this._playApproach = false;

		this._heartBeatSFX = [
			'sfx_heartbeat_slow_00',
			'sfx_heartbeat_medium_00',
			'sfx_heartbeat_fast_00',
			'sfx_heartbeat_vfast_00',
		];
		this._heartBeatTimes = [];

		switch (common.difficulty) {
		default:
		case 'easy':
			this._pinterDifficulty = { lower: 0, upper: 3 };
			break;
		case 'medium':
			this._pinterDifficulty = { lower: 0, upper: 4 };
			break;
		case 'hard':
			this._pinterDifficulty = { lower: 0, upper: 5 };
			break;
		}
	}

	destroy() {
		if (this._approachSFX) {
			this._approachSFX.stop();
			P3.audio.cleanupAudio(this._approachSFX);
		}
		this._approachSFX = null;

		this._disaster.destroy();
	}

	stopSounds() {
		this._disaster.stopSounds();
	}

	resize() {
		this._disaster.resize();
	}

	loadData(data) {
		this._type = data.type || Scenario.TYPE_TSUNAMI;
		this._difficulty = common.difficulty;
		this._time = data.difficulty[common.difficulty].time || 300;

		this._budget = data.difficulty[common.difficulty].budget;
		this._population = data.difficulty[common.difficulty].population;

		this._targets = {
			housing: data.difficulty[common.difficulty].housingTarget,
			hospital: data.difficulty[common.difficulty].hospitals,
			school: data.difficulty[common.difficulty].schools,
			mission: data.difficulty[common.difficulty].mission,
		};
		this._targetProgress = {
			housing: this._population.housed,
			hospital: 0,
			school: 0,
			mission: 0,
		};
		this._limits = {
			deaths: data.difficulty[common.difficulty].deathLimit,
			damage: data.difficulty[common.difficulty].damageLimit,
		};

		switch (this._type) {
		case Scenario.TYPE_TSUNAMI : {
			this._disaster = new Tsunami(this._world);
			this._sfx = [
				'sfx_ts_random_fx_00',
				'sfx_ts_random_fx_01',
				'sfx_ts_random_fx_02',
				'sfx_ts_random_fx_03',
				'sfx_ts_random_fx_04',
				'sfx_ts_random_fx_05',
				'sfx_ts_random_fx_06',
			];
			this._approachSFX = 'sfx_ts_disaster_approaching_loop_00';
			break;
		}
		case Scenario.TYPE_EARTHQUAKE : {
			this._disaster = new Earthquake(this._world);
			this._sfx = [
				'sfx_eq_random_fx_00',
				'sfx_eq_random_fx_01',
				'sfx_eq_random_fx_02',
				'sfx_eq_random_fx_03',
				'sfx_eq_random_fx_04',
				'sfx_eq_random_fx_05',
				'sfx_eq_random_fx_07',
			];
			this._approachSFX = 'sfx_eq_disaster_approaching_loop_00';
			break;
		}
		case Scenario.TYPE_FLOOD : {
			this._disaster = new Flood(this._world);
			this._sfx = [
				'sfx_fd_random_fx_00',
				'sfx_fd_random_fx_01',
				'sfx_fd_random_fx_02',
				'sfx_fd_random_fx_03',
				'sfx_fd_random_fx_04',
				'sfx_fd_random_fx_05',
				'sfx_fd_random_fx_06',
				'sfx_fd_random_fx_07',
			];
			this._approachSFX = 'sfx_fd_disaster_approaching_loop_00';
			break;
		}
		case Scenario.TYPE_WILD_FIRE : {
			this._disaster = new Wildfire(this._world);
			this._sfx = [
				'sfx_wf_random_fx_00',
				'sfx_wf_random_fx_01',
				'sfx_wf_random_fx_02',
				'sfx_wf_random_fx_03',
				'sfx_wf_random_fx_04',
				'sfx_wf_random_fx_05',
				'sfx_wf_random_fx_06',
				'sfx_wf_random_fx_07',
				'sfx_wf_random_fx_08',
			];
			this._approachSFX = 'sfx_wf_disaster_approaching_loop_00';
			break;
		}
		case Scenario.TYPE_HURRICANE : {
			this._disaster = new Hurricane(this._world);
			this._sfx = [
				'sfx_hr_random_fx_00',
				'sfx_hr_random_fx_01',
				'sfx_hr_random_fx_02',
				'sfx_hr_random_fx_03',
				'sfx_hr_random_fx_04',
				'sfx_hr_random_fx_05',
				'sfx_hr_random_fx_06',
			];
			this._approachSFX = 'sfx_hr_disaster_approaching_loop_00';
			break;
		}
		default:
			this._disaster = new Earthquake(this._world);
			break;
		}

		this._approachSFX = P3.audio.playSound(this._approachSFX, { loop: true });
		// this._warningSFX = P3.audio.playSound('sfx_disaster_imminent_waning_loop_00', {loop: true});
		this._approachSFX.stop();
		// this._warningSFX.stop();

		for (let i = 0; i < 4; i++) {
			this._heartBeatTimes.push(((this._time / 4) - 20) * (i + 1));
		}
		this._heartbeatDelay = 1.5;
		this._previousObjectiveCount = 0;
	}

	playHeartbeat(index) {
		P3.audio.playSound(this._heartBeatSFX[index]);
	}

	updateTime() {
		if (Date.now() - this._countdownTime > 1000 && (!this._finished || !this._ended)) {	// 1 second has passed
			if (this._elapsedTime >= this._time) {
				this._disaster.canStart = true;
				this._finished = true;
				this.signals.timerend.dispatch();
			}
			this._countdownTime = Date.now();
			this._elapsedTime++;
			this._elapsedTime = clamp(this._elapsedTime, 0, this._time);

			const index = this._heartBeatTimes.indexOf(this._elapsedTime);

			if (index > -1) {
				this.playHeartbeat(index);
				P3.utils.delay(this._heartbeatDelay, () => {
					this.playHeartbeat(index);
					P3.utils.delay(this._heartbeatDelay, () => {
						this.playHeartbeat(index);
						this._heartbeatDelay -= 0.3;
					});
				});
			}
			if (!this._playApproach && (this._elapsedTime > this._time - 40) && (common.selectedLevel === 'hurricane' || common.selectedLevel === 'flood')) {
				this._disaster.darken(35000);
				this._playApproach = true;
				this._disaster.canRain = true;
				// this._warningSFX.play();
				this._approachSFX.play();
			}
			if (this._elapsedTime > this._time - 15 && !this._playApproach) {
				this._playApproach = true;
				this._approachSFX.play();
				// this._warningSFX.play();
			}
		}

		if (Date.now() - this._lastSfxPlay > P3.math.randomInt(20000, 30000)) {
			this._lastSfxPlay = Date.now();
			const i = P3.math.randomInt(0, this._sfx.length - 1);

			P3.audio.playSound(this._sfx[i]);
		}
	}

	updateObjectives() {
		let objectives = 0;

		for (const i in this._targets) {
			if (this._targetProgress[i] >= this._targets[i]) {
				objectives++;
			}
		}

		if (this._previousObjectiveCount < objectives) {
			P3.audio.playSound('sfx_goal_achieved_00');
			this._previousObjectiveCount = objectives;
		}

		if (objectives >= 4) {
			this._disaster.canStart = true;
			this._hasCompletedObjectives = true;
			this.signals.objectivescomplete.dispatch('objectives');
		} else {
			this._disaster.canStart = false;
			this._hasCompletedObjectives = false;
		}
	}

	onBuild(type) {
		if (type === 'hospital') {
			this._targetProgress.hospital++;
		} else if (type === 'school') {
			this._targetProgress.school++;
		} else if (type === 'hotel' && common.selectedLevel === 'tsunami') {
			this._targetProgress.mission++;
		}

		this._targetProgress.housing = this._population.housed;

		this.updateObjectives();
	}

	onUpgrade() {
		this._targetProgress.mission++;
		this.updateObjectives();
	}

	onDestroy(type) {
		switch (type) {
		case 'hospital':
			this._targetProgress.hospital--;
			break;
		case 'hotel':
			this._targetProgress.mission--;
			break;
		case 'school':
			this._targetProgress.school--;
			break;
		default:
			break;
		}
		this._targetProgress.housing = this._population.housed;

		this.updateObjectives();
	}

	determineOutcome() {
		this._win = this._hasCompletedObjectives && !(
			this._disaster.hospitalDestroyed
			|| this._disaster.schoolDestroyed
			|| this._disaster.totalDeaths >= this._limits.deaths
			|| this._disaster.totalCost >= this._limits.damage
				|| (this._targetProgress.housing < this._targets.housing));

		common.tracking.track(new P3.TrackingDataGtagEvent('complete_level', {
			event_category: 'UI',
			event_type: this._win ? 'level_end_success' : 'level_end_failure',
			page_container: common.selectedLevel,
		}));
	}

	start() {
		// override
	}

	update(dt) {
		this._disaster && this._disaster.update(dt);
		this.updateTime();
	}

	get world() {
		return this._world;
	}

	get disaster() {
		return this._disaster;
	}

	get type() {
		return this._type;
	}

	get difficulty() {
		return this._difficulty;
	}

	get ended() {
		return this._ended;
	}

	get elapsedTime() {
		return this._elapsedTime;
	}

	get score() {
		return this._score;
	}

	get limits() {
		return this._limits;
	}

	get targets() {
		return this._targets;
	}

	get targetProgress() {
		return this._targetProgress;
	}

	get time() {
		return this._time;
	}

	get budget() {
		return this._budget;
	}

	get population() {
		return this._population;
	}

	get objectivesComplete() {
		return this._hasCompletedObjectives;
	}

	get objectivesCount() {
		return this._objectives;
	}

	set budget(budget) {
		this._budget = budget;
	}

	set population(population) {
		this._population = population;
	}

	get win() {
		return this._win;
	}

	get finished() {
		return this._finished;
	}

	set finished(value) {
		this._finished = value;
	}

	set ended(value) {
		this._ended = value;
	}

	get ccUpgrades() {
		return this._ccUpgrades;
	}

	set ccUpgrades(v) {
		this._ccUpgrades = v;
	}

	set elapsedTime(v) {
		this._elapsedTime = v;
	}
}

Scenario.TYPE_TSUNAMI = 'ts';
Scenario.TYPE_EARTHQUAKE = 'eq';
Scenario.TYPE_FLOOD = 'fd';
Scenario.TYPE_HURRICANE = 'hr';
Scenario.TYPE_WILD_FIRE = 'wf';
